import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, User as UserIcon, LogOut } from "lucide-react";
import { openWhatsApp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { href: "#tiers", label: "Services" },
  { href: "#which-saarthi", label: "Which One?" },
  { href: "#cases", label: "Real Deals" },
  { href: "#calculator", label: "Savings" },
  { href: "#story", label: "Story" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Section links (#tiers, #story, etc.) only exist on the homepage. From any
  // other page, clicking them must navigate home first, then scroll once mounted.
  function goToSection(hash) {
    return (e) => {
      e.preventDefault();
      if (location.pathname === "/") {
        const el = document.querySelector(hash);
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" });
      } else {
        navigate(`/${hash}`);
      }
    };
  }

  function goHome(e) {
    e.preventDefault();
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  }

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.8, 0, 0.2, 1], delay: 0.3 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-5 md:px-10 py-4">
        <a href="/" onClick={goHome} className="flex items-center gap-2.5" data-testid="brand-logo">
          <img src="/logo.svg" alt="CaarSaarthi" className="w-9 h-9 rounded-full" />
          <span className="font-serif text-2xl md:text-[26px] tracking-tight">CaarSaarthi</span>
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) =>
            l.href.startsWith("/") ? (
              <Link
                key={l.href}
                to={l.href}
                className="text-sm text-silver/70 hover:text-white transition-colors duration-200"
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={`/${l.href}`}
                onClick={goToSection(l.href)}
                className="text-sm text-silver/70 hover:text-white transition-colors duration-200"
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            )
          )}
        </nav>
        <button
          onClick={() => openWhatsApp({ source: "navbar" })}
          className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white gradient-bg hover:scale-[1.03] transition-transform duration-200 shadow-[0_8px_30px_rgba(139,92,246,0.35)]"
          data-testid="nav-whatsapp-btn"
        >
          WhatsApp Us
        </button>
        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <Link
                to={user.role === "admin" ? "/admin/dashboard" : "/dashboard"}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-sm text-white hover:bg-white/5 transition-colors"
                data-testid="nav-dashboard"
              >
                <UserIcon className="w-4 h-4" /> Dashboard
              </Link>
              <button onClick={logout} className="p-2 text-silver/60 hover:text-white" data-testid="nav-logout" aria-label="Logout">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm text-silver/80 hover:text-white px-3 py-2" data-testid="nav-login">Sign in</Link>
              <Link to="/signup" className="text-sm px-4 py-2 rounded-full border border-white/15 text-white hover:bg-white/5" data-testid="nav-signup">Get started</Link>
            </>
          )}
        </div>
        <button
          className="md:hidden p-2 text-silver"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden glass border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col gap-4">
              {LINKS.map((l) =>
                l.href.startsWith("/") ? (
                  <Link key={l.href} to={l.href} onClick={() => setOpen(false)} className="text-silver/80 py-1" data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
                    {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.href}
                    href={`/${l.href}`}
                    onClick={(e) => { setOpen(false); goToSection(l.href)(e); }}
                    className="text-silver/80 py-1"
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </a>
                )
              )}
              <button
                onClick={() => {
                  setOpen(false);
                  openWhatsApp({ source: "navbar_mobile" });
                }}
                className="mt-2 px-5 py-3 rounded-full text-sm font-medium text-white gradient-bg"
                data-testid="mobile-nav-whatsapp"
              >
                WhatsApp Us
              </button>
              {user ? (
                <Link to={user.role === "admin" ? "/admin/dashboard" : "/dashboard"} onClick={() => setOpen(false)} className="mt-2 px-5 py-3 rounded-full text-sm font-medium border border-white/15 text-white text-center" data-testid="mobile-nav-dashboard">
                  My Dashboard
                </Link>
              ) : (
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <Link to="/login" onClick={() => setOpen(false)} className="py-3 rounded-full text-sm border border-white/15 text-center text-white" data-testid="mobile-nav-login">Sign in</Link>
                  <Link to="/signup" onClick={() => setOpen(false)} className="py-3 rounded-full text-sm gradient-bg text-center text-white" data-testid="mobile-nav-signup">Get started</Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
